import { Stack, useTheme } from "@mui/material";
import { useAskHelenaConversations } from "controller";
import { useAtom, useAtomValue } from "jotai";
import {
  type AiResultActionType,
  recordAction,
  trackAiHome,
} from "../../analytics";
import { useAskHelena } from "../hooks/useAskHelena";
import { useAskHelenaConversationHistory } from "../hooks/useAskHelenaConversationHistory";
import { useChatConversationActions } from "../hooks/useChatConversationActions";
import {
  askHelenaConversationId,
  askHelenaMessages,
  chatDraftAtom,
  chatDraftVoiceSessionIdAtom,
  hasOpenConversationAtom,
  isAskHelenaAwaitingConfirmation,
  isChatSearchOpenAtom,
  selectedConversationIdAtom,
} from "../store";
import { ChatComposer } from "./ChatComposer";
import { ChatPanelHeader } from "./ChatPanelHeader";
import { ChatSearchPanel } from "./ChatSearchPanel";
import type { ChatRowAction } from "./ChatSidebarItem";
import { ChatThreadArea } from "./ChatThreadArea";

const UNTITLED_THREAD_TITLE = "Untitled";

/** Dictated text goes after whatever is already typed — nothing is replaced. */
const appendDictation = (draft: string, dictated: string): string =>
  draft.trim() ? `${draft.trimEnd()} ${dictated}` : dictated;

export const ChatPanel = () => {
  const theme = useTheme();
  const selectedConversationId = useAtomValue(selectedConversationIdAtom);
  const liveConversationId = useAtomValue(askHelenaConversationId);
  const [draft, setDraft] = useAtom(chatDraftAtom);
  const [voiceSessionId, setVoiceSessionId] = useAtom(
    chatDraftVoiceSessionIdAtom,
  );
  const isSearchOpen = useAtomValue(isChatSearchOpenAtom);
  const messages = useAtomValue(askHelenaMessages);
  const isAwaitingConfirmation = useAtomValue(isAskHelenaAwaitingConfirmation);
  const hasOpenConversation = useAtomValue(hasOpenConversationAtom);
  const { send, confirm, cancel, stop, isBusy, isStreaming } = useAskHelena();
  const actions = useChatConversationActions();

  // Fetches the thread opened from history and hands it to the state machine,
  // so it renders through the same components as a live turn.
  const { isLoading, errorMessage } = useAskHelenaConversationHistory(
    selectedConversationId,
  );

  // Already in cache for the sidebar — this is a cache read, not a second fetch.
  const { data } = useAskHelenaConversations();

  const hasLiveConversation = messages.length > 0;
  const activeConversationId = liveConversationId ?? selectedConversationId;

  const listedConversation = data?.items.find(
    (conversation) => conversation.id === activeConversationId,
  );

  // The service names threads best-effort, a second or two after the first
  // turn, and some it never names at all. Echoing the user's own question back
  // as the title of the thread they are reading is noise, so it waits.
  const threadTitle =
    listedConversation?.title?.trim() || UNTITLED_THREAD_TITLE;

  const handleSend = () => {
    const question = draft;
    setDraft("");
    setVoiceSessionId(null);
    void send(question, "composer", {
      voiceSessionId: voiceSessionId ?? undefined,
    });
  };

  // An emptied draft no longer holds the dictated words, so the recording
  // must not be linked to whatever gets typed next.
  const handleDraftChange = (next: string) => {
    setDraft(next);
    if (!next.trim()) setVoiceSessionId(null);
  };

  const handleDictationAccept = (text: string, sessionId: string) => {
    setDraft((current) => appendDictation(current, text));
    setVoiceSessionId(sessionId);
  };

  const handleSuggestion = (prompt: string) => {
    void send(prompt, "suggested_prompt");
  };

  const trackPendingWriteAction = (
    pendingWriteId: string,
    action: AiResultActionType,
  ) => {
    const pendingWrite = messages.find(
      (message) => message.pendingWrite?.pendingWriteId === pendingWriteId,
    )?.pendingWrite;

    recordAction(action);
    trackAiHome("ai_result_action_taken", {
      chat_id: activeConversationId,
      action_type: action,
      result_type: pendingWrite?.label ?? null,
      item_id: pendingWriteId,
    });
  };

  const handleThreadAction = (action: ChatRowAction) => {
    if (!activeConversationId) return;

    switch (action) {
      case "pin":
        void actions.pin(activeConversationId);
        break;
      case "unpin":
        void actions.unpin(activeConversationId);
        break;
      case "delete":
        actions.requestDelete(activeConversationId);
        break;
      // Rename is handled inside the header, which owns the editor.
      case "rename":
        break;
    }
  };

  return (
    <Stack
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        p: "16px 0px",
        backgroundColor: theme.palette.primitive.system.violet[15],
      }}
    >
      {/* Search takes the whole panel — composing has no meaning while browsing
          history, so the composer goes with it. */}
      {isSearchOpen ? (
        <ChatSearchPanel />
      ) : (
        <>
          {hasOpenConversation && (
            <ChatPanelHeader
              title={threadTitle}
              rawTitle={listedConversation?.title ?? null}
              isPinned={listedConversation?.pinnedAt != null}
              // A first question is on screen before the `conversation` event
              // lands; there is nothing to pin, rename or delete until then.
              canAct={!!activeConversationId}
              onAction={handleThreadAction}
              onRename={(title) => {
                if (activeConversationId) {
                  void actions.rename(activeConversationId, title);
                }
              }}
            />
          )}

          <ChatThreadArea
            isLoading={isLoading}
            errorMessage={errorMessage}
            hasLiveConversation={hasLiveConversation}
            onAskSuggestion={handleSuggestion}
            onApprovePendingWrite={(pendingWriteId) => {
              trackPendingWriteAction(pendingWriteId, "approve_pending_write");
              void confirm(pendingWriteId);
            }}
            onDeclinePendingWrite={(pendingWriteId) => {
              trackPendingWriteAction(pendingWriteId, "decline_pending_write");
              void cancel(pendingWriteId);
            }}
          />

          <ChatComposer
            value={draft}
            isBusy={isBusy || isLoading}
            isStreaming={isStreaming}
            isAwaitingConfirmation={isAwaitingConfirmation}
            onChange={handleDraftChange}
            onSend={handleSend}
            onStop={stop}
            onDictationAccept={handleDictationAccept}
          />
        </>
      )}
    </Stack>
  );
};
